import type { VehicleEstimationRequest } from "@/types/valuation";
import type { PriceSectionVehicleInfo } from "@/utils/priceSectionVehicleInfo";
import { resolveIsNotImported } from "@/utils/importFlag";

/**
 * Build the AutoUncle estimation request from the post-an-ad form's vehicle info.
 *
 * Pure and exported so the payload can be asserted directly: this is the path where a
 * hand-built prop literal used to strip trim / isNotImported / origin / equipment before
 * they ever reached the request.
 *
 * Equipment is the dealer's CURRENT selection (car.info prefill plus any dealer edits) -
 * the flags are derived from it server-side; an empty list is sent as undefined so the
 * backend omits the hash entirely.
 */
export function toVehicleEstimationRequest(
  vehicleInfo: PriceSectionVehicleInfo
): VehicleEstimationRequest {
  return {
    mileage: vehicleInfo.mileage as number,
    registrationDate: vehicleInfo.registrationDate || "",
    regNumber: vehicleInfo.regNumber as string,
    make: vehicleInfo.make as string,
    model: vehicleInfo.model as string,
    // Enhanced fields for better estimation
    vin: vehicleInfo.vin,
    year: vehicleInfo.year ? parseInt(vehicleInfo.year, 10) : undefined,
    variant: vehicleInfo.variant,
    trim: vehicleInfo.trim,
    // Prefers the raw tri-state from the lookup, else derives from a Swedish origin.
    isNotImported: resolveIsNotImported(vehicleInfo.isNotImported, vehicleInfo.origin),
    equipment:
      vehicleInfo.equipment && vehicleInfo.equipment.length > 0
        ? vehicleInfo.equipment
        : undefined,
    fuelType: vehicleInfo.fuelType,
    bodyType: vehicleInfo.bodyType,
    transmission: vehicleInfo.transmission,
    engineVolume: vehicleInfo.engineVolume,
    electricDriveRange: vehicleInfo.electricDriveRange,
    horsePower: vehicleInfo.horsepower ? parseInt(vehicleInfo.horsepower, 10) : undefined,
  };
}
